import type { ProductConfigField } from '../types';

/**
 * Die Konfigurationsfelder eines Produkts als Formular.
 *
 * `ProductConfigField` stand seit Langem im Datenmodell und kam über
 * `GET /products/:id` mit — nur fragte die Oberfläche die Felder nirgends ab.
 * Die Werte landen in `currentConfig` und von dort über `{{KEY}}` in die
 * `.env` der Vorlage. Anlass: die eLibrary (Demo-Set, Marke, Federation-Token
 * je Instanz), 09.10.2026.
 *
 * `validation.options` (Liste) macht aus einem Feld eine Auswahl.
 */
export default function ProduktKonfigFelder({
  felder,
  werte,
  setWerte,
}: {
  felder: ProductConfigField[];
  werte: Record<string, string>;
  setWerte: (w: Record<string, string>) => void;
}) {
  const sichtbar = felder.filter((f) => f.visibleInPC).sort((a, b) => a.sortOrder - b.sortOrder);
  if (!sichtbar.length) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {sichtbar.map((f) => {
        const optionen = Array.isArray((f.validation as { options?: unknown })?.options)
          ? ((f.validation as { options: string[] }).options)
          : null;
        const hinweis = (f.validation as { hint?: string } | null)?.hint;
        const wert = werte[f.key] ?? '';
        return (
          <label key={f.key} className="block text-sm">
            <span className="text-xs font-medium text-slate-600">
              {f.label}{f.required && <span className="text-red-600"> *</span>}
            </span>
            {optionen ? (
              <select
                value={wert}
                onChange={(e) => setWerte({ ...werte, [f.key]: e.target.value })}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-surface px-2 py-1.5 text-sm"
              >
                {!f.required && <option value="">—</option>}
                {optionen.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                type={f.isSecret ? 'password' : f.type === 'color' ? 'text' : 'text'}
                value={wert}
                onChange={(e) => setWerte({ ...werte, [f.key]: e.target.value })}
                placeholder={f.defaultValue ?? ''}
                autoComplete="off"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-surface px-2 py-1.5 text-sm"
              />
            )}
            {hinweis && <span className="mt-0.5 block text-[11px] text-slate-500">{hinweis}</span>}
          </label>
        );
      })}
    </div>
  );
}

/** Vorgabewerte der Felder, ergänzt um schon vorhandene Werte. */
export function startWerte(felder: ProductConfigField[], vorhanden: Record<string, unknown> = {}): Record<string, string> {
  const w: Record<string, string> = {};
  for (const f of felder) {
    const v = vorhanden[f.key];
    w[f.key] = typeof v === 'string' ? v : v != null ? String(v) : f.defaultValue ?? '';
  }
  return w;
}

/** Pflichtfelder ohne Wert — für die Sperre des Knopfs. */
export function fehlendePflicht(felder: ProductConfigField[], werte: Record<string, string>): string[] {
  return felder.filter((f) => f.visibleInPC && f.required && !(werte[f.key] ?? '').trim()).map((f) => f.label);
}
